import type React from "react"
import { useState, useEffect, useMemo } from "react"
import { Button, Form, Modal, Spinner, Alert, Row, Col, InputGroup, Table } from "react-bootstrap"
import { FaPlus, FaEdit, FaToggleOn, FaToggleOff, FaGift, FaUsers, FaSearch } from "react-icons/fa"
import Swal from "sweetalert2"
import { motion, AnimatePresence } from "framer-motion"
import loyaltyCommissionService from "../../../services/LoyaltyCommissionService"
import "../../../css/GestionCommissions.css"

interface LoyaltyCommission {
  id: number
  commissionPercentage: number
  isActive: boolean
  description?: string
  validFrom?: string
  validUntil?: string
}

interface CommissionUser {
  userId: number
  userName: string
  userEmail?: string
  createdAt: string
  commission: LoyaltyCommission | null
}

interface LoyaltyCommissionData {
  commissionPercentage: number
  description?: string
  validFrom?: string
  validUntil?: string
}

const cardAnim = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
}

const btnAnim = {
  rest: { scale: 1 },
  hover: { scale: 1.04, boxShadow: "0px 2px 12px #2a919d33" },
}

function formatCommission(val: any) {
  const value = Number(val)
  return isNaN(value) ? "0.00" : value.toFixed(2)
}

const GestionCommissionUser: React.FC = () => {
  const [users, setUsers] = useState<CommissionUser[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [editingUser, setEditingUser] = useState<CommissionUser | null>(null)
  const [commissionPercentage, setCommissionPercentage] = useState<number>(0)
  const [initialCommission, setInitialCommission] = useState<number>(0)
  const [description, setDescription] = useState<string>("")
  const [initialDescription, setInitialDescription] = useState<string>("")
  const [validFrom, setValidFrom] = useState<string>("")
  const [initialValidFrom, setInitialValidFrom] = useState<string>("")
  const [validUntil, setValidUntil] = useState<string>("")
  const [initialValidUntil, setInitialValidUntil] = useState<string>("")
  const [applyAllMode, setApplyAllMode] = useState<boolean>(false)
  const [applyAllSubmitting, setApplyAllSubmitting] = useState<boolean>(false)
  const [globalCommission, setGlobalCommission] = useState<number>(0)
  const [initialGlobalCommission, setInitialGlobalCommission] = useState<number>(0)
  const [globalDescription, setGlobalDescription] = useState<string>("")
  const [globalValidFrom, setGlobalValidFrom] = useState<string>("")
  const [globalValidUntil, setGlobalValidUntil] = useState<string>("")
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [isDailyView, setIsDailyView] = useState(false)
  const [originalUsers, setOriginalUsers] = useState<CommissionUser[]>([])

  useEffect(() => {
    fetchAllUsersCommissions()
  }, [])

  const fetchAllUsersCommissions = async () => {
    setLoading(true)
    setError(null)

    try {
      const res = await loyaltyCommissionService.getAllUsersCommissions()
      if (res.success && Array.isArray(res.users)) {
        const normalized = res.users.map((u: any) => ({
          ...u,
          commission: u.commission
            ? {
                ...u.commission,
                commissionPercentage: Number(u.commission.commissionPercentage),
              }
            : null,
        }))
        setUsers(normalized)
        setOriginalUsers(normalized)
      } else {
        setError("Impossible de charger la liste des commissions.")
        setUsers([])
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Impossible de charger la liste des commissions.")
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  const openModal = (user: CommissionUser | null = null) => {
    setEditingUser(user)
    if (user && user.commission) {
      const commission = Number(user.commission.commissionPercentage)
      const desc = user.commission.description || ""
      const vFrom = user.commission.validFrom ? user.commission.validFrom.split("T")[0] : ""
      const vUntil = user.commission.validUntil ? user.commission.validUntil.split("T")[0] : ""
      setCommissionPercentage(commission)
      setInitialCommission(commission)
      setDescription(desc)
      setInitialDescription(desc)
      setValidFrom(vFrom)
      setInitialValidFrom(vFrom)
      setValidUntil(vUntil)
      setInitialValidUntil(vUntil)
    } else {
      setCommissionPercentage(0)
      setInitialCommission(0)
      setDescription("")
      setInitialDescription("")
      setValidFrom("")
      setInitialValidFrom("")
      setValidUntil("")
      setInitialValidUntil("")
    }
    setShowModal(true)
  }

  const resetForm = () => {
    setCommissionPercentage(0)
    setInitialCommission(0)
    setDescription("")
    setInitialDescription("")
    setValidFrom("")
    setInitialValidFrom("")
    setValidUntil("")
    setInitialValidUntil("")
  }

  const validateForm = (value: number): boolean => !(value < 0 || value > 100)

  const hasFormChanged = (): boolean => {
    return (
      commissionPercentage !== initialCommission ||
      description !== initialDescription ||
      validFrom !== initialValidFrom ||
      validUntil !== initialValidUntil
    )
  }

  const handleCreateOrUpdateCommission = async () => {
    if (!editingUser || !validateForm(commissionPercentage)) return
    setSubmitting(true)
    try {
      const data: LoyaltyCommissionData = {
        commissionPercentage,
        description: description.trim() || undefined,
        validFrom: validFrom || undefined,
        validUntil: validUntil || undefined,
      }
      await loyaltyCommissionService.createOrUpdateUserCommission(editingUser.userId, data)
      setShowModal(false)
      resetForm()
      await fetchAllUsersCommissions()
      let message = ""
      if (commissionPercentage === 0) {
        message = `Commission désactivée pour ${editingUser.userName} (pourcentage à 0%)`
      } else if (editingUser.commission) {
        message = `Commission modifiée pour ${editingUser.userName}`
      } else {
        message = `Commission créée pour ${editingUser.userName}`
      }
      Swal.fire({
        title: "Succès",
        text: message,
        icon: "success",
        confirmButtonColor: "#2A919D",
        timer: 2000,
      })
    } catch (err: any) {
      Swal.fire({
        title: "Erreur",
        text: err.response?.data?.message || "Erreur lors de l'opération",
        icon: "error",
        confirmButtonColor: "#2A919D",
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleStatus = async (user: CommissionUser) => {
    if (!user.commission) return
    try {
      await loyaltyCommissionService.toggleUserCommissionStatus(user.userId, user.commission.id)
      await fetchAllUsersCommissions()
      Swal.fire({
        title: "Succès",
        text: `Commission ${!user.commission.isActive ? "activée" : "désactivée"} pour ${user.userName}`,
        icon: "success",
        confirmButtonColor: "#2A919D",
        timer: 2000,
      })
    } catch (err: any) {
      Swal.fire({
        title: "Erreur",
        text: err.response?.data?.message || "Impossible de changer le statut de la commission",
        icon: "error",
        confirmButtonColor: "#2A919D",
      })
    }
  }

  const openApplyAll = () => {
    setApplyAllMode(true)
    setGlobalCommission(0)
    setInitialGlobalCommission(0)
    setGlobalDescription("")
    setGlobalValidFrom("")
    setGlobalValidUntil("")
  }

  const cancelApplyAll = () => {
    setApplyAllMode(false)
    setGlobalCommission(0)
    setInitialGlobalCommission(0)
    setGlobalDescription("")
    setGlobalValidFrom("")
    setGlobalValidUntil("")
  }

  const handleApplyToAll = async () => {
    if (!validateForm(globalCommission)) return
    let confirmText = `Êtes-vous sûr de vouloir appliquer une commission de ${globalCommission}% à tous les utilisateurs ?`
    if (globalCommission === 0) {
      confirmText = "Êtes-vous sûr de vouloir désactiver toutes les commissions (0%) pour tous les utilisateurs ?"
    }
    const result = await Swal.fire({
      title: "Confirmation",
      text: confirmText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2A919D",
      cancelButtonColor: "#d33",
      confirmButtonText: "Oui, appliquer",
      cancelButtonText: "Annuler",
    })
    if (!result.isConfirmed) return
    setApplyAllSubmitting(true)
    try {
      const data: LoyaltyCommissionData = {
        commissionPercentage: globalCommission,
        description: globalDescription.trim() || undefined,
        validFrom: globalValidFrom || undefined,
        validUntil: globalValidUntil || undefined,
      }
      await loyaltyCommissionService.applyCommissionToAllUsers(data)
      setApplyAllMode(false)
      setGlobalCommission(0)
      setInitialGlobalCommission(0)
      setGlobalDescription("")
      setGlobalValidFrom("")
      setGlobalValidUntil("")
      await fetchAllUsersCommissions()
      const message =
        globalCommission === 0
          ? "Toutes les commissions ont été désactivées (0%)."
          : "Commission appliquée à tous les utilisateurs."
      Swal.fire({
        title: "Succès",
        text: message,
        icon: "success",
        confirmButtonColor: "#2A919D",
        timer: 2000,
      })
    } catch (err: any) {
      Swal.fire({
        title: "Erreur",
        text: err.response?.data?.message || "Erreur lors de l'opération",
        icon: "error",
        confirmButtonColor: "#2A919D",
      })
    } finally {
      setApplyAllSubmitting(false)
    }
  }

  const handleDisableAllCommissions = async () => {
    const result = await Swal.fire({
      title: "Confirmation",
      text: "Voulez-vous vraiment désactiver toutes les commissions ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2A919D",
      cancelButtonColor: "#d33",
      confirmButtonText: "Oui, désactiver tout",
      cancelButtonText: "Annuler",
    })
    if (!result.isConfirmed) return
    setApplyAllSubmitting(true)
    try {
      await loyaltyCommissionService.disableAllCommissions()
      await fetchAllUsersCommissions()
      Swal.fire({
        title: "Succès",
        text: "Toutes les commissions ont été désactivées.",
        icon: "success",
        confirmButtonColor: "#2A919D",
        timer: 2000,
      })
    } catch (err: any) {
      Swal.fire({
        title: "Erreur",
        text: err.response?.data?.message || "Impossible de désactiver toutes les commissions.",
        icon: "error",
        confirmButtonColor: "#2A919D",
      })
    } finally {
      setApplyAllSubmitting(false)
    }
  }

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users
    const term = searchTerm.trim().toLowerCase()
    return users.filter((u) => {
      const name = u.userName.toLowerCase()
      const email = (u.userEmail || "").toLowerCase()
      const percent = u.commission ? formatCommission(u.commission.commissionPercentage) : "0.00"
      const status =
        u.commission && u.commission.commissionPercentage > 0
          ? u.commission.isActive
            ? "active"
            : "inactive"
          : "sans commission"
      return name.includes(term) || email.includes(term) || percent.includes(term) || status.includes(term)
    })
  }, [users, searchTerm])

  const closeModal = () => {
    setShowModal(false)
    setEditingUser(null)
    resetForm()
  }

  return (
    <div className="gc-user-container" style={{ background: "#f8f9fa", minHeight: "100vh", padding: "10px" }}>
      <motion.div
        className="gc-user-header-section d-flex justify-content-between align-items-center flex-wrap mb-3"
        initial={{ opacity: 0, y: -24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="d-flex align-items-center flex-wrap gap-2">
          <h2 className="gc-user-page-title" style={{ color: "#2a919d", margin: 0 }}>
            <FaUsers className="me-2 gc-user-header-icon" /> Gestion des Commissions de fidélité
          </h2>
          <p className="gc-user-page-subtitle" style={{ color: "#2a919d" }}>
            Gérez les commissions individuelles ou appliquez une commission à tous les utilisateurs.
          </p>
        </div>
      </motion.div>

      <div
        className="gc-action-bar p-3 mb-3"
        style={{
          backgroundColor: "#f9fafb",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <div
          className="d-flex flex-wrap justify-content-between align-items-center"
          style={{ gap: "15px" }}
        >
          <div className="d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
            <span className="badge" style={{ fontSize: "0.95rem", backgroundColor: "#4BB0C0", color: "white" }}>
              👥 {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? "s" : ""}
            </span>
            <span
              className="badge"
              style={{
                backgroundColor: "#4BB0C0",
                fontSize: "0.95rem",
                color: "white",
              }}
            >
              💰 {users.filter((u) => u.commission?.isActive).length} commission
              {users.filter((u) => u.commission?.isActive).length > 1 ? "s" : ""} active
            </span>
          </div>

          <div className="d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
            <motion.button
              className="btn"
              style={{
                backgroundColor: isDailyView ? "#4BB0C0" : "#F4A261",
                color: "white",
                borderRadius: "8px",
                padding: "10px 15px",
                fontWeight: 500,
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (isDailyView) {
                  setUsers(originalUsers)
                  setIsDailyView(false)
                } else {
                  const today = new Date()
                  const filtered = originalUsers.filter(
                    (u) =>
                      u.commission &&
                      u.commission.isActive &&
                      (!u.commission.validFrom || new Date(u.commission.validFrom) <= today) &&
                      (!u.commission.validUntil || new Date(u.commission.validUntil) >= today)
                  )
                  filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  setUsers(filtered)
                  setIsDailyView(true)
                }
              }}
            >
              <FaUsers className="me-2" />
              {isDailyView ? " Revenir à la liste" : "Commission du jour"}
            </motion.button>

            <motion.button
              className="btn btn-outline-danger"
              style={{ borderRadius: "8px", padding: "10px 15px", fontWeight: 500 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDisableAllCommissions}
              disabled={applyAllSubmitting || users.every((u) => !u.commission?.isActive)}
            >
              <FaToggleOff className="me-2" />
              Désactiver toutes
            </motion.button>

            <motion.button
              className="btn"
              style={{ borderRadius: "8px", padding: "10px 15px", fontWeight: 500, backgroundColor: "#2a919d", color: "white" }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={openApplyAll}
              disabled={users.length === 0}
            >
              <FaGift className="me-2" />
              Appliquer à tous
            </motion.button>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
          <strong>Erreur:</strong> {error}
        </Alert>
      )}

      {loading ? (
        <div className="loading-container text-center py-5">
          <Spinner animation="border" role="status" variant="#2a919d">
            <span className="visually-hidden">Chargement…</span>
          </Spinner>
          <p className="mt-2 text-muted">Chargement des commissions...</p>
        </div>
      ) : (
        <>
          <Form className="mb-4">
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Rechercher nom, email, statut ou %"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Form>

          <AnimatePresence>
            {applyAllMode && (
              <motion.div initial="hidden" animate="visible" exit="exit" variants={cardAnim} className="mb-4">
                <div className="gc-user-commission-input-container mb-4">
                  <Form>
                    <Row>
                      <Col md={3} xs={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Pourcentage de commission</Form.Label>
                          <Form.Control
                            type="number"
                            min={0}
                            max={100}
                            step={0.01}
                            value={globalCommission}
                            onChange={(e) => setGlobalCommission(Number.parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                            required
                          />
                          <Form.Text className="text-muted">
                            {globalCommission === 0 ? "0% désactivera toutes les commissions" : "Entre 0 et 100%"}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={3} xs={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            type="text"
                            value={globalDescription}
                            onChange={(e) => setGlobalDescription(e.target.value)}
                            placeholder="Description optionnelle"
                            maxLength={255}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3} xs={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Date de début</Form.Label>
                          <Form.Control
                            type="date"
                            value={globalValidFrom}
                            onChange={(e) => setGlobalValidFrom(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3} xs={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Date de fin</Form.Label>
                          <Form.Control
                            type="date"
                            value={globalValidUntil}
                            onChange={(e) => setGlobalValidUntil(e.target.value)}
                            min={globalValidFrom || undefined}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      variant="outline-secondary"
                      onClick={cancelApplyAll}
                      disabled={applyAllSubmitting}
                      style={{ fontWeight: 600, borderRadius: 10 }}
                    >
                      Annuler
                    </Button>
                    <motion.button
                      className="btn"
                      style={{
                        background: "#2a919d",
                        color: "white",
                        fontWeight: 600,
                        border: "none",
                        borderRadius: 10,
                        padding: "8px 24px",
                      }}
                      variants={btnAnim}
                      whileHover="hover"
                      whileTap="rest"
                      disabled={applyAllSubmitting || globalCommission < 0 || globalCommission > 100}
                      onClick={handleApplyToAll}
                    >
                      {applyAllSubmitting ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" /> Application en cours...
                        </>
                      ) : (
                        <>
                          <FaGift className="me-1" /> Appliquer à tous
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {filteredUsers.length === 0 ? (
            <div className="gc-user-card text-center py-5">
              <div>
                <FaUsers size={48} className="gc-user-no-commission-icon mb-3" />
                <h5 className="gc-user-no-commission">Aucun utilisateur trouvé</h5>
                <p className="gc-user-no-commission">Il n'y a actuellement aucun utilisateur dans le système.</p>
              </div>
            </div>
          ) : (
            <div className="table-responsive mb-4">
              <Table responsive hover striped className="gc-commission-table">
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Email</th>
                    <th style={{ width: 120 }}>Commission %</th>
                    <th style={{ width: 120 }}>Statut</th>
                    <th>Début</th>
                    <th>Fin</th>
                    <th>Description</th>
                    <th style={{ width: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {filteredUsers.map((user) => {
                      const c = user.commission
                      const hasCommission = !!c && Number(c.commissionPercentage) !== 0
                      const isExpired = c?.validUntil && new Date(c.validUntil) < new Date()
                      const isInactive = c && (Number(c.commissionPercentage) === 0 || !c.isActive)
                      return (
                        <motion.tr key={user.userId} variants={cardAnim} initial="hidden" animate="visible" exit="exit">
                          <td style={{ verticalAlign: "middle" }}>
                            <div className="fw-bold">{user.userName}</div>
                          </td>
                          <td style={{ verticalAlign: "middle" }}>{user.userEmail || "-"}</td>
                          <td style={{ verticalAlign: "middle" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <FaGift />
                              <div>
                                <div className="fw-bold">{c ? formatCommission(c.commissionPercentage.toFixed(2)) : "0.00"}</div>
                                <small className="text-muted">%</small>
                              </div>
                            </div>
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            {c ? (
                              <span className={`badge ${isInactive ? "bg-secondary" : "bg-primary"}`}>
                                {Number(c.commissionPercentage) === 0 ? "Désactivée (0%)" : c.isActive ? "Active" : "Inactive"}
                              </span>
                            ) : (
                              <span className="text-muted small">Aucune</span>
                            )}
                            {isExpired && <div className="mt-1 small text-secondary">Expirée</div>}
                          </td>
                          <td style={{ verticalAlign: "middle" }}>{c?.validFrom ? new Date(c.validFrom).toLocaleDateString("fr-FR") : "-"}</td>
                          <td style={{ verticalAlign: "middle" }}>{c?.validUntil ? new Date(c.validUntil).toLocaleDateString("fr-FR") : "-"}</td>
                          <td style={{ verticalAlign: "middle" }}>{c?.description ? c.description : "-"}</td>
                          <td style={{ verticalAlign: "middle" }}>
                            <div style={{ display: "flex", gap: 8 }}>
                              <motion.button
                                className="btn"
                                style={{
                                  background: "white",
                                  color: "#2a919d",
                                  border: "2px solid #2a919d",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  fontWeight: 600,
                                }}
                                variants={btnAnim}
                                whileHover="hover"
                                whileTap="rest"
                                onClick={() => openModal(user)}
                              >
                                {hasCommission ? (
                                  <>
                                    <FaEdit className="me-1" /> Modifier
                                  </>
                                ) : (
                                  <>
                                    <FaPlus className="me-1" /> Attribuer
                                  </>
                                )}
                              </motion.button>

                              <motion.button
                                className="btn btn-outline-secondary"
                                style={{ borderRadius: 8, padding: "6px 10px", display: "flex", alignItems: "center", gap: 6 }}
                                whileHover={{ scale: 1.03 }}
                                onClick={() => handleToggleStatus(user)}
                                disabled={!user.commission}
                                title={user.commission ? (user.commission.isActive ? "Désactiver" : "Activer") : "Aucune commission à activer"}
                              >
                                {user.commission && user.commission.isActive ? <FaToggleOn /> : <FaToggleOff />}
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                      )
                    })}
                  </AnimatePresence>
                </tbody>
              </Table>
            </div>
          )}

          <Modal show={showModal} onHide={closeModal} backdrop="static" size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>
                {editingUser && editingUser.commission && Number(editingUser.commission.commissionPercentage) > 0
                  ? `Modifier la commission de ${editingUser.userName}`
                  : `Attribuer une commission à ${editingUser?.userName || ""}`}
              </Modal.Title>
            </Modal.Header>
            <Form>
              <Modal.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Pourcentage de commission *</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        value={commissionPercentage}
                        onChange={(e) => setCommissionPercentage(Number.parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                        required
                      />
                      <Form.Text className="text-muted">
                        {commissionPercentage === 0 ? "0% désactivera la commission" : "Entre 0 et 100%"}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Description optionnelle"
                        maxLength={255}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date de début</Form.Label>
                      <Form.Control type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
                      <Form.Text className="text-muted">Optionnel - Si vide, effective immédiatement</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date de fin</Form.Label>
                      <Form.Control
                        type="date"
                        value={validUntil}
                        onChange={(e) => setValidUntil(e.target.value)}
                        min={validFrom || undefined}
                      />
                      <Form.Text className="text-muted">Optionnel - Si vide, pas de date d'expiration</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="outline-secondary"
                  onClick={closeModal}
                  disabled={submitting}
                  style={{ fontWeight: 600, borderRadius: 10 }}
                >
                  Annuler
                </Button>
                <motion.button
                  className="btn"
                  style={{
                    background: "#2a919d",
                    color: "white",
                    fontWeight: 600,
                    border: "none",
                    borderRadius: 10,
                    padding: "9px 20px",
                  }}
                  variants={btnAnim}
                  whileHover="hover"
                  whileTap="rest"
                  type="button"
                  onClick={handleCreateOrUpdateCommission}
                  disabled={submitting || commissionPercentage < 0 || commissionPercentage > 100 || !hasFormChanged()}
                >
                  {submitting ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      {editingUser && editingUser.commission ? "Modification..." : "Attribution..."}
                    </>
                  ) : editingUser && editingUser.commission ? (
                    <>
                      <FaEdit className="me-1" /> Modifier
                    </>
                  ) : (
                    <>
                      <FaPlus className="me-1" /> Attribuer
                    </>
                  )}
                </motion.button>
              </Modal.Footer>
            </Form>
          </Modal>
        </>
      )}
    </div>
  )
}

export default GestionCommissionUser
